import { overlay } from 'overlay-kit';
import { useCallback } from 'react';

import { Label } from '@/components/shadcn/ui/label';
import { CHART_TERM_TYPE } from '@/entities/chart/model/constants';
import { createDefaultTemplateFixedDateTerm } from '@/entities/dashboard-template';
import { productSentimentFormConfig } from '@/entities/dashboard-template/templates/product-sentiment-topic/form';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { withForm } from '@/shared/ui/form';

import { TemplateDateTermField } from '../../../ui/template-date-term-field';
import { ProductFilterField } from './product-filter-field';
import { ScrapingJobSection } from './scraping-job-section';
import { TaxonomyFilterField } from './taxonomy-filter-field';

export const ProductSentimentFormBody = withForm({
  defaultValues: productSentimentFormConfig.defaultValues,
  render: function ProductSentimentFormBodyRender({ form }) {
    // 제네릭 auto-fill 핸들러: key-value 기반으로 폼 값 머지
    const handleAutoFill = useCallback(
      (params: {
        productFilter: FilterKeyValue[];
        dateRange: { startDate: string; endDate: string };
      }) => {
        // productFilter: 중복 제거 후 머지
        const currentProducts = form.state.values.productFilter;
        const existingKeys = new Set(
          currentProducts.map((v) => v.filterKey),
        );
        const newProducts = params.productFilter.filter(
          (v) => !existingKeys.has(v.filterKey),
        );
        form.setFieldValue('productFilter', [
          ...currentProducts,
          ...newProducts,
        ]);

        // dateTerm: auto-fill은 fixed 개념으로 강제
        const currentDateTerm = form.state.values.dateTerm;
        const currentFixedTerm =
          currentDateTerm.type === CHART_TERM_TYPE.FIXED
            ? currentDateTerm
            : createDefaultTemplateFixedDateTerm();

        const startDates = [
          currentFixedTerm.startDate,
          params.dateRange.startDate,
        ].filter(Boolean);
        const endDates = [currentFixedTerm.endDate, params.dateRange.endDate].filter(
          Boolean,
        );

        form.setFieldValue('dateTerm', {
          type: CHART_TERM_TYPE.FIXED,
          startDate: startDates.length > 0 ? startDates.sort()[0] : '',
          endDate:
            endDates.length > 0
              ? endDates.sort()[endDates.length - 1]
              : '',
        });
      },
      [form],
    );

    // overlay-kit으로 scraping job dialog 열기
    const openAutoFillDialog = useCallback(() => {
      overlay.open(({ isOpen: dialogOpen, close, unmount }) => (
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            if (!open) close();
          }}
          onExitComplete={unmount}
        >
          <DialogContent size="m" className="overflow-hidden p-0">
            <div className="flex h-full flex-col overflow-hidden">
              <DialogHeader className="shrink-0">
                <DialogTitle>Import from scraping jobs</DialogTitle>
                <DialogDescription>
                  Select items to auto-fill the form.
                </DialogDescription>
              </DialogHeader>

              <DialogBody className="flex-1 overflow-y-auto py-4">
                <ScrapingJobSection
                  onJobsSelected={(params) => {
                    handleAutoFill({
                      productFilter: params.productFilter,
                      dateRange: params.dateRange,
                    });
                    close();
                  }}
                />
              </DialogBody>

              <DialogFooter className="shrink-0 border-t bg-white">
                <div />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => close()}
                  >
                    Close
                  </Button>
                </div>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      ));
    }, [handleAutoFill]);

    return (
      <>
        {/* Dashboard Title */}
        <form.AppField
          name="title"
          children={(field) => (
            <field.LabelGroup label="Dashboard Title" required>
              <field.TextField placeholder="Enter dashboard title" />
            </field.LabelGroup>
          )}
        />

        {/* Products (+ auto-fill 버튼) */}
        <form.AppField
          name="productFilter"
          children={(field) => (
            <div className="grid w-full items-center gap-2">
              <div className="flex items-center justify-between">
                <Label>Products<span className="text-destructive ml-1">*</span></Label>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={openAutoFillDialog}
                  className="h-auto p-0 text-xs"
                >
                  Import from scraping jobs
                </Button>
              </div>
              <ProductFilterField
                value={field.state.value}
                onChange={(v) => field.setValue(v)}
              />
            </div>
          )}
        />

        {/* Topics */}
        <form.AppField
          name="topicFilter"
          children={(field) => (
            <div className="grid w-full items-center gap-2">
              <Label>Topics</Label>
              <TaxonomyFilterField
                value={field.state.value}
                onChange={(v) => field.setValue(v)}
              />
            </div>
          )}
        />

        {/* Date Range */}
        <form.AppField
          name="dateTerm"
          children={(field) => (
            <TemplateDateTermField
              value={field.state.value}
              onChange={(nextValue) => field.setValue(nextValue)}
              errors={field.state.meta.errors}
            />
          )}
        />
      </>
    );
  },
});
